import { axiosInstance } from '../../../App';
import { GetItemsProps } from 'medere-ui-framework';
import {
  AgendaAppointment, AgendaProfessional, AgendaSchedule, AgendaSpeciality, AgendaStats,
  MonthlyCalendarResponse,
  PatientSearchResult,
  BookAppointmentRequest, BookAppointmentResponse, ComboOption, PaginatedResponse,
  PatientStatusResponse, AppointmentPractice,
  ScheduleSetup, ChangeLocationRequest, PatientAppointmentWarning,
  AppointmentLogEntryDTO, PriorityDTO, ScheduleTagDTO,
} from '../types/agenda.types';
import { AppointmentsDTO } from '../../../interface/interface';
import {
  AvailableCheck, BillingConceptOption, CoverageOption, CreateInvoiceRequest, CreateInvoiceResponse, InvoicePreview,
  InvoicePreviewRequest, InvoicingStatusResponse, MessageOptions, OrderDraft, OrderValorization, PatientOperation,
  PatientPaymentRequest, PatientPaymentResponse, PaymentOptions, PendingBillingOrder, PendingInvoice,
  RegisterOrderRequest, RegisterOrderResponse, SelectOption,
} from '../types/billing.types';
import {
  EvolutionPanel, SavePanelValue, EvolutionInitData, EditLogEntry,
} from '../types/evolution.types';

const BASE_URL = '/api/secured/agenda';

export interface CreateRequestingDoctorRequest {
  firstName: string;
  lastName: string;
  workerEnrollment: string;
}

export function getRequestingDoctors({
  filterValue = '',
  offSet = 0,
  pageSize = 50,
}: GetItemsProps): Promise<{ id: number; name: string }[]> {
  return axiosInstance.get<{ id: number; name: string }[]>(`${BASE_URL}/requesting-doctors`, {
    params: { filter: filterValue, offset: offSet, pageSize },
  }).then((response) => response.data);
}

export function createExternalRequestingDoctor(
  request: CreateRequestingDoctorRequest,
): Promise<{ id: number; name: string }> {
  return axiosInstance.post<{ id: number; name: string }>(
    `${BASE_URL}/requesting-doctors/external`,
    request,
  ).then((response) => response.data);
}

export function getCoverageLimited(): Promise<{ limited: boolean }> {
  return axiosInstance.get<{ limited: boolean }>(`${BASE_URL}/coverage-limited`)
    .then((response) => response.data);
}

export function getHealthEntityAppointmentsCategory(healthEntityId: number): Promise<{ category: string }> {
  return axiosInstance.get<{ category: string }>(`${BASE_URL}/health-entities/${healthEntityId}/appointments-category`)
    .then((response) => response.data);
}

export function getAgendaAppointments(
  date: string,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  scheduleTemplateIds?: number[] | null,
  offset: number = 0,
  pageSize: number = 50,
  stateFilter?: number[] | null,
  coverageFilter?: string | null,
  locationFilter?: number | null,
  appointmentTypeFilter?: number | null,
  buildingId?: number | null,
): Promise<PaginatedResponse<AgendaAppointment>> {
  return axiosInstance.get<PaginatedResponse<AgendaAppointment>>(`${BASE_URL}/appointments`, {
    params: {
      date,
      offset,
      pageSize,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(stateFilter && stateFilter.length > 0 ? { stateFilter: stateFilter.join(',') } : {}),
      ...(coverageFilter ? { coverageFilter } : {}),
      ...(locationFilter ? { locationFilter } : {}),
      ...(appointmentTypeFilter ? { appointmentTypeFilter } : {}),
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

export function getAgendaSchedule(
  date: string,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  offset: number = 0,
  pageSize: number = 50,
  scheduleTemplateIds?: number[] | null,
  includeFutureDays?: boolean,
  appointmentsCategory?: string | null,
  buildingId?: number | null,
  qtyAppointments: number = 1,
  isNewPatient: boolean = false,
): Promise<PaginatedResponse<AppointmentsDTO>> {
  return axiosInstance.get<PaginatedResponse<AppointmentsDTO>>(`${BASE_URL}/schedule`, {
    params: {
      date,
      offset,
      pageSize,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(includeFutureDays ? { includeFutureDays: true } : {}),
      ...(appointmentsCategory ? { appointmentsCategory } : {}),
      ...(buildingId ? { buildingId } : {}),
      qtyAppointments,
      isNewPatient,
    },
  }).then((response) => response.data);
}

export function getAgendaSchedules(
  professionalId?: number | null,
  specialityId?: number | null,
  filter?: string,
): Promise<AgendaSchedule[]> {
  return axiosInstance.get<AgendaSchedule[]>(`${BASE_URL}/schedules`, {
    params: {
      ...(professionalId && professionalId > 0 ? { professionalId } : {}),
      ...(specialityId && specialityId > 0 ? { specialityId } : {}),
      ...(filter ? { filter } : {}),
    },
  }).then((response) => response.data);
}

export function getAgendaOptions(filter?: string, date?: string, buildingId?: number | null, spontaneous?: boolean): Promise<AgendaSchedule[]> {
  return axiosInstance.get<AgendaSchedule[]>(`${BASE_URL}/agendas`, {
    params: {
      ...(filter ? { filter } : {}),
      ...(date ? { date } : {}),
      ...(buildingId ? { buildingId } : {}),
      ...(spontaneous ? { spontaneous: true } : {}),
    },
  }).then((response) => response.data);
}

export function getSpontaneousSetups(templateIds: number[], date: string, buildingId?: number | null): Promise<ScheduleSetup[]> {
  return axiosInstance.get<ScheduleSetup[]>(`${BASE_URL}/schedule-setups/spontaneous`, {
    params: {
      templateIds: templateIds.join(','),
      date,
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

export function getAgendaLocations(scheduleTemplateIds: number[]): Promise<{ id: number; name: string }[]> {
  return axiosInstance.get<{ id: number; name: string }[]>(`${BASE_URL}/agendas/locations`, {
    params: { scheduleTemplateIds: scheduleTemplateIds.join(',') },
  }).then((response) => response.data);
}

export function getAgendaSummary(
  date: string,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  scheduleTemplateIds?: number[] | null,
  buildingId?: number | null,
): Promise<AgendaStats> {
  return axiosInstance.get<AgendaStats>(`${BASE_URL}/summary`, {
    params: {
      date,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

// ─── Range-based queries (week/month views) ──────────

export function getAgendaAppointmentsRange(
  dateFrom: string,
  dateTo: string,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  scheduleTemplateIds?: number[] | null,
  offset: number = 0,
  pageSize: number = 200,
  stateFilter?: number[] | null,
  coverageFilter?: string | null,
  buildingId?: number | null,
): Promise<PaginatedResponse<AgendaAppointment>> {
  return axiosInstance.get<PaginatedResponse<AgendaAppointment>>(`${BASE_URL}/appointments`, {
    params: {
      dateFrom,
      dateTo,
      offset,
      pageSize,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(stateFilter && stateFilter.length > 0 ? { stateFilter: stateFilter.join(',') } : {}),
      ...(coverageFilter ? { coverageFilter } : {}),
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

export function getAgendaSummaryRange(
  dateFrom: string,
  dateTo: string,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  scheduleTemplateIds?: number[] | null,
  buildingId?: number | null,
): Promise<AgendaStats> {
  return axiosInstance.get<AgendaStats>(`${BASE_URL}/summary`, {
    params: {
      dateFrom,
      dateTo,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

export function getMonthlyCalendarSummary(
  year: number,
  month: number,
  professionalIds?: number[] | null,
  specialityIds?: number[] | null,
  scheduleTemplateIds?: number[] | null,
  buildingId?: number | null,
): Promise<MonthlyCalendarResponse> {
  return axiosInstance.get<MonthlyCalendarResponse>(`${BASE_URL}/monthly-summary`, {
    params: {
      year,
      month,
      ...(professionalIds && professionalIds.length > 0 ? { professionalIds: professionalIds.join(',') } : {}),
      ...(specialityIds && specialityIds.length > 0 ? { specialityIds: specialityIds.join(',') } : {}),
      ...(scheduleTemplateIds && scheduleTemplateIds.length > 0 ? { scheduleTemplateIds: scheduleTemplateIds.join(',') } : {}),
      ...(buildingId ? { buildingId } : {}),
    },
  }).then((response) => response.data);
}

export function getAgendaProfessionals(
  filter?: string
): Promise<AgendaProfessional[]> {
  return axiosInstance.get<AgendaProfessional[]>(`${BASE_URL}/professionals`, {
    params: { filter: filter || '' },
  }).then((response) => response.data);
}

export function registerArrival(appointmentId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/arrive`);
}

export function getPatientStatus(appointmentId: number): Promise<PatientStatusResponse> {
  return axiosInstance.get<PatientStatusResponse>(`${BASE_URL}/appointments/${appointmentId}/patient-status`)
    .then((response) => response.data);
}

export function assignPatientAndArrive(appointmentId: number, patientId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/assign-patient-and-arrive`, { patientId });
}

export function getAppointmentPractices(appointmentId: number): Promise<AppointmentPractice[]> {
  return axiosInstance.get<AppointmentPractice[]>(`${BASE_URL}/appointments/${appointmentId}/practices`)
    .then((response) => response.data);
}

export function checkInAppointment(
  appointmentId: number,
  patientId: number,
  practices: AppointmentPractice[],
): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/check-in`, {
    patientId,
    practices: practices.map((practice) => ({
      id: practice.id,
      nomenclatorId: practice.nomenclatorId,
      requestingDoctorId: practice.requestingDoctorId,
      practiceDate: practice.practiceDate,
      observations: practice.observations,
    })),
  });
}

export function cancelAppointment(appointmentId: number, reason: string): Promise<{ cancelledReminders: number }> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/cancel`, { reason })
    .then((response) => response.data);
}

export function callPatient(appointmentId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/call`);
}

export function finishAttention(appointmentId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/appointments/${appointmentId}/finish`);
}

export function updateAppointmentNotes(appointmentId: number, notes: string): Promise<void> {
  return axiosInstance.put(`${BASE_URL}/appointments/${appointmentId}/notes`, { notes });
}

export function getPatientById(patientId: number): Promise<PatientSearchResult> {
  return axiosInstance.get<PatientSearchResult>(`${BASE_URL}/patients/${patientId}`)
    .then((response) => response.data);
}

export function searchPatients(query: string): Promise<PatientSearchResult[]> {
  return axiosInstance.get<PatientSearchResult[]>(`${BASE_URL}/patients/search`, {
    params: { query },
  }).then((response) => response.data);
}

export function bookAppointment(data: BookAppointmentRequest): Promise<BookAppointmentResponse> {
  return axiosInstance.post<BookAppointmentResponse>(`${BASE_URL}/appointments/book`, data)
    .then((response) => response.data);
}

export function getPatientAppointmentWarnings(
  patientId: number,
  professionalId: number,
): Promise<PatientAppointmentWarning[]> {
  return axiosInstance.get<PatientAppointmentWarning[]>(
    `${BASE_URL}/patients/${patientId}/professionals/${professionalId}/appointment-warnings`,
  ).then((response) => response.data);
}

export function getHealthEntities(filter?: string, offset?: number, pageSize?: number): Promise<ComboOption[]> {
  return axiosInstance.get<ComboOption[]>(`${BASE_URL}/health-entities`, {
    params: { filter: filter || undefined, offset, pageSize },
  }).then((response) => response.data);
}

export function getHealthEntityPlans(healthEntityId: number): Promise<ComboOption[]> {
  return axiosInstance.get<ComboOption[]>(`${BASE_URL}/health-entities/${healthEntityId}/plans`)
    .then((response) => response.data);
}

export function bookMultisession(data: {
  patientId?: number;
  patientReference?: string;
  patientPhone?: string;
  patientDocument?: string;
  professionalId?: number | null;
  specialityId?: number | null;
  notes?: string;
  sessions: { date: string; time: string; scheduleSetupId: number; duration: number }[];
  notifyWhatsApp?: boolean;
  notifyEmail?: boolean;
  buildingId?: number | null;
}): Promise<{ count: number; appointmentIds: number[] }> {
  return axiosInstance.post(`${BASE_URL}/appointments/book-multisession`, data)
    .then((response) => response.data);
}

export function getCurrentUserProfessional(): Promise<AgendaProfessional | null> {
  return axiosInstance.get(`${BASE_URL}/me/professional`)
    .then((response) => {
      if (response.status === 204) return null;
      return response.data as AgendaProfessional;
    })
    .catch(() => null);
}

// Billing endpoints
const BILLING_URL = '/api/secured/agenda/billing';

/** Valoriza la orden del turno; sin filas carga las prácticas del turno y las del grupo de la agenda. */
export function valorizeOrder(appointmentId: number, draft: OrderDraft): Promise<OrderValorization> {
  return axiosInstance.post(`${BILLING_URL}/appointments/${appointmentId}/valorize`, draft).then((r) => r.data);
}

export function registerOrder(appointmentId: number, data: RegisterOrderRequest): Promise<RegisterOrderResponse> {
  return axiosInstance.post(`${BILLING_URL}/appointments/${appointmentId}/register-order`, data).then((r) => r.data);
}

/** Operaciones de órdenes del paciente del turno, para "Asociar". */
export function getPatientOperations(appointmentId: number): Promise<PatientOperation[]> {
  return axiosInstance.get(`${BILLING_URL}/appointments/${appointmentId}/patient-operations`).then((r) => r.data);
}

export function associateOrdersToAppointment(appointmentId: number, operationIds: number[]): Promise<void> {
  return axiosInstance.post(`${BILLING_URL}/appointments/${appointmentId}/associate-orders`, { operationIds }).then(() => undefined);
}

export function hasAppointmentOrder(appointmentId: number): Promise<{ hasOrder: boolean; orderId?: number; orderDate?: string }> {
  return axiosInstance.get(`${BILLING_URL}/appointments/${appointmentId}/has-order`)
    .then((r) => r.data);
}

export function getPatientCoverages(appointmentId: number): Promise<CoverageOption[]> {
  return axiosInstance.get(`${BILLING_URL}/appointments/${appointmentId}/patient-coverages`)
    .then((r) => r.data);
}

export function getAttentionModalities(): Promise<SelectOption[]> {
  return axiosInstance.get(`${BILLING_URL}/modalities`)
    .then((r) => r.data);
}

/** Conceptos de facturación activos ("Agregar otros conceptos"), para el combo con búsqueda. */
export function searchBillingConcepts({ filterValue, offSet, pageSize }: { filterValue?: string; offSet?: number; pageSize?: number }): Promise<BillingConceptOption[]> {
  return axiosInstance.get(`${BILLING_URL}/billing-concepts`, {
    params: { filter: filterValue ?? '', offset: offSet ?? 0, pageSize: pageSize ?? 20 },
  }).then((r) => r.data);
}

/** Edificios activos del sitio para la orden del turno, con el sugerido marcado como default. */
export function getBuildingOptions(appointmentId: number): Promise<SelectOption[]> {
  return axiosInstance.get(`${BILLING_URL}/appointments/${appointmentId}/buildings`).then((r) => r.data);
}

/** Órdenes del paciente del turno que todavía no se facturaron. */
export function getPendingBillingOrders(appointmentId: number): Promise<PendingBillingOrder[]> {
  return axiosInstance.get(`${BILLING_URL}/appointments/${appointmentId}/pending-billing-orders`).then((r) => r.data);
}

/** Datos para facturar la orden y sus montos con lo elegido (condición de IVA, tipo, IVA de cada ítem, IIBB). */
export function getInvoicePreview(orderId: number, request: InvoicePreviewRequest = {}): Promise<InvoicePreview> {
  return axiosInstance.post(`${BILLING_URL}/orders/${orderId}/invoice-preview`, request).then((r) => r.data);
}

/** Alícuota de percepción de IIBB del padrón para el CUIT ("Buscar alícuota en el padrón"). */
export function getIibbAliquot(document: string): Promise<number> {
  return axiosInstance.get(`${BILLING_URL}/iibb-aliquot`, { params: { document } }).then((r) => r.data.percentage);
}

export function getPosForOrder(orderId: number, formTypeId: number): Promise<SelectOption[]> {
  return axiosInstance.get(`${BILLING_URL}/orders/${orderId}/pos`, { params: { formTypeId } }).then((r) => r.data);
}

export function createInvoice(orderId: number, data: CreateInvoiceRequest): Promise<CreateInvoiceResponse> {
  return axiosInstance.post(`${BILLING_URL}/orders/${orderId}/create-invoice`, data).then((r) => r.data);
}

export function getInvoicingStatus(logId: number): Promise<InvoicingStatusResponse> {
  return axiosInstance.get(`${BILLING_URL}/invoicing-status/${logId}`).then((r) => r.data);
}

export function getPaymentOptions(): Promise<PaymentOptions> {
  return axiosInstance.get(`${BILLING_URL}/payment-options`).then((r) => r.data);
}

/** Cheques de cartera que todavía no están en ningún recibo. */
export function getAvailableChecks(): Promise<AvailableCheck[]> {
  return axiosInstance.get(`${BILLING_URL}/checks/available`).then((r) => r.data);
}

export function getPendingInvoices(patientId: number): Promise<PendingInvoice[]> {
  return axiosInstance.get(`${BILLING_URL}/patients/${patientId}/pending-invoices`).then((r) => r.data);
}

export function registerPatientPayment(data: PatientPaymentRequest): Promise<PatientPaymentResponse> {
  return axiosInstance.post(`${BILLING_URL}/payments`, data).then((r) => r.data);
}

/** PDF de una factura o recibo. */
export function getFormPdf(adminFormId: number): Promise<Blob> {
  return axiosInstance.get(`${BILLING_URL}/forms/${adminFormId}/pdf`, { responseType: 'blob' })
    .then((r) => new Blob([r.data], { type: 'application/pdf' }));
}

export function getMessageOptions(patientId: number): Promise<MessageOptions> {
  return axiosInstance.get(`${BILLING_URL}/patients/${patientId}/message-options`).then((r) => r.data);
}

export function sendForms(data: { patientId: number; adminFormIds: number[]; messageTypeId: number; to: string }): Promise<{ message: string }> {
  return axiosInstance.post(`${BILLING_URL}/forms/send`, data).then((r) => r.data);
}

// Evolution endpoints

export function getEvolutionInit(appointmentId: number): Promise<EvolutionInitData> {
  return axiosInstance.get<EvolutionInitData>(`${BASE_URL}/evolution/${appointmentId}/init`)
    .then((response) => response.data);
}

export function saveEvolution(
  appointmentId: number,
  scoreTemplateId: number | null,
  previousPatientControlId: number | null,
  panels: SavePanelValue[],
  draftPatientControlId?: number | null,
): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/evolution/${appointmentId}/save`, {
    scoreTemplateId,
    previousPatientControlId,
    panels,
    draftPatientControlId: draftPatientControlId ?? null,
  });
}

export function saveDraft(
  appointmentId: number,
  scoreTemplateId: number | null,
  previousPatientControlId: number | null,
  panels: SavePanelValue[],
  draftPatientControlId?: number | null,
): Promise<{ patientControlId: number | null }> {
  return axiosInstance
    .post<{ success: boolean; patientControlId: number | null }>(
      `${BASE_URL}/evolution/${appointmentId}/save-draft`,
      { scoreTemplateId, previousPatientControlId, panels, draftPatientControlId: draftPatientControlId ?? null },
    )
    .then((response) => ({ patientControlId: response.data.patientControlId }));
}

export function markAsMissed(appointmentId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/evolution/${appointmentId}/missed`);
}

export function returnToQueue(appointmentId: number): Promise<void> {
  return axiosInstance.post(`${BASE_URL}/evolution/${appointmentId}/return-to-queue`);
}

export interface RevertStatusResponse {
  appointmentId: number;
  previousStateId: number;
  newStateId: number;
  newStateName: string;
}

export function revertAppointmentStatus(appointmentId: number): Promise<RevertStatusResponse> {
  return axiosInstance.post<RevertStatusResponse>(`${BASE_URL}/appointments/${appointmentId}/revert-status`)
    .then((response) => response.data);
}

export function getEditLog(
  patientId: number,
  patientControlId: number,
): Promise<EditLogEntry[]> {
  return axiosInstance
    .get<EditLogEntry[]>(`/api/secured/patients/${patientId}/clinical-history/${patientControlId}/edit-log`)
    .then((response) => response.data);
}

export function getEditEvolutionInit(
  patientId: number,
  patientControlId: number,
): Promise<EvolutionInitData> {
  return axiosInstance
    .get<EvolutionInitData>(`/api/secured/patients/${patientId}/clinical-history/${patientControlId}/edit-init`)
    .then((response) => response.data);
}

export function saveEditEvolution(
  patientId: number,
  patientControlId: number,
  scoreTemplateId: number | null,
  previousPatientControlId: number | null,
  panels: SavePanelValue[],
): Promise<void> {
  return axiosInstance.post(
    `/api/secured/patients/${patientId}/clinical-history/${patientControlId}/edit-save`,
    { scoreTemplateId, previousPatientControlId, panels },
  );
}

export function getAvailablePanels(): Promise<EvolutionPanel[]> {
  return axiosInstance.get<EvolutionPanel[]>(`${BASE_URL}/evolution/available-panels`)
    .then((response) => response.data);
}

export function getProfessionalAppointmentTypes(professionalId: number): Promise<{ id: number; name: string }[]> {
  return axiosInstance.get<{ id: number; name: string }[]>(`${BASE_URL}/professionals/${professionalId}/appointment-types`)
    .then((response) => response.data);
}

export function getProfessionalLocations(professionalId: number): Promise<{ id: number; name: string }[]> {
  return axiosInstance.get<{ id: number; name: string }[]>(`${BASE_URL}/professionals/${professionalId}/locations`)
    .then((response) => response.data);
}

export function getSpecialityLocations(specialityId: number): Promise<{ id: number; name: string }[]> {
  return axiosInstance.get<{ id: number; name: string }[]>(`${BASE_URL}/specialities/${specialityId}/locations`)
    .then((response) => response.data);
}

// ─── Change location endpoints ──────────────────────

export function getScheduleSetups(professionalId: number): Promise<ScheduleSetup[]> {
  return axiosInstance
    .get<ScheduleSetup[]>(`${BASE_URL}/professionals/${professionalId}/schedule-setups`)
    .then((response) => response.data);
}

export function getAvailableLocations(setupIds: number[]): Promise<ComboOption[]> {
  return axiosInstance
    .get<ComboOption[]>(`${BASE_URL}/schedule-setups/available-locations`, {
      params: { setupIds: setupIds.join(',') },
    })
    .then((response) => response.data);
}

export function changeSetupLocation(data: ChangeLocationRequest): Promise<void> {
  return axiosInstance.put(`${BASE_URL}/schedule-setups/location`, data);
}

// ─── Appointment log ────────────────────────────────

export function getAppointmentLog(appointmentId: number): Promise<AppointmentLogEntryDTO[]> {
  return axiosInstance
    .get<AppointmentLogEntryDTO[]>(`${BASE_URL}/appointments/${appointmentId}/log`)
    .then((response) => response.data);
}

// ─── Priorities ─────────────────────────────────────

export function getPriorities(): Promise<PriorityDTO[]> {
  return axiosInstance
    .get<PriorityDTO[]>(`${BASE_URL}/priorities`)
    .then((response) => response.data);
}

export function assignPriority(appointmentId: number, priorityId: number): Promise<void> {
  return axiosInstance.put(`${BASE_URL}/appointments/${appointmentId}/priority`, { priorityId });
}

// ─── Export / Print ─────────────────────────────────

export interface AppointmentExportFilters {
  date: string;
  professionalId?: number | null;
  specialityId?: number | null;
  stateFilter?: number[] | null;
  coverageFilter?: string | null;
  locationFilter?: number | null;
  appointmentTypeFilter?: number | null;
  buildingId?: number | null;
}

function buildExportParams(filters: AppointmentExportFilters): Record<string, string | number> {
  const params: Record<string, string | number> = { date: filters.date };
  if (filters.professionalId) params.professionalId = filters.professionalId;
  if (filters.specialityId) params.specialityId = filters.specialityId;
  if (filters.stateFilter && filters.stateFilter.length > 0) params.stateFilter = filters.stateFilter.join(',');
  if (filters.coverageFilter) params.coverageFilter = filters.coverageFilter;
  if (filters.locationFilter) params.locationFilter = filters.locationFilter;
  if (filters.appointmentTypeFilter) params.appointmentTypeFilter = filters.appointmentTypeFilter;
  if (filters.buildingId) params.buildingId = filters.buildingId;
  return params;
}

export function printAppointmentsPdf(filters: AppointmentExportFilters): Promise<Blob> {
  return axiosInstance
    .get(`${BASE_URL}/appointments/print-pdf`, {
      params: buildExportParams(filters),
      responseType: 'blob',
    })
    .then((response) => response.data);
}

export function exportAppointmentsExcel(filters: AppointmentExportFilters): Promise<Blob> {
  return axiosInstance
    .get(`${BASE_URL}/appointments/export-excel`, {
      params: buildExportParams(filters),
      responseType: 'blob',
    })
    .then((response) => response.data);
}

// Specialities
export function getAgendaSpecialities(
  filter?: string
): Promise<AgendaSpeciality[]> {
  return axiosInstance.get<AgendaSpeciality[]>(`${BASE_URL}/specialities`, {
    params: { filter: filter || '' },
  }).then((response) => response.data);
}

// Combobox-compatible functions (follow GetItemsProps interface)
export function getAgendaProfessionalsCombo(props: { offSet?: number; pageSize?: number; filterValue?: string }): Promise<AgendaProfessional[]> {
  return getAgendaProfessionals(props.filterValue || '');
}

export function getAgendaSpecialitiesCombo(props: { offSet?: number; pageSize?: number; filterValue?: string }): Promise<AgendaSpeciality[]> {
  return getAgendaSpecialities(props.filterValue || '');
}

export function getAgendaSchedulesCombo(props: { offSet?: number; pageSize?: number; filterValue?: string; params?: { professionalId?: number; specialityId?: number } }): Promise<AgendaSchedule[]> {
  if (!props.params?.professionalId && !props.params?.specialityId) return Promise.resolve([]);
  return getAgendaSchedules(
    props.params?.professionalId ?? null,
    props.params?.specialityId ?? null,
    props.filterValue || ''
  );
}

export function getHealthEntitiesCombo(props: { offSet?: number; pageSize?: number; filterValue?: string }): Promise<ComboOption[]> {
  return getHealthEntities(props.filterValue, props.offSet, props.pageSize);
}

export function getHealthEntityPlansCombo(props: { offSet?: number; pageSize?: number; filterValue?: string; params?: { healthEntityId: number } }): Promise<ComboOption[]> {
  if (!props.params?.healthEntityId) return Promise.resolve([]);
  return getHealthEntityPlans(props.params.healthEntityId);
}

export function getFirstAvailableDate(
  scheduleTemplateIds: number[],
  fromDate: string,
  buildingId?: number | null,
  appointmentsCategory?: string | null,
  qtyAppointments: number = 1,
  isNewPatient: boolean = false,
): Promise<{ date: string | null }> {
  return axiosInstance.get<{ date: string | null }>(`${BASE_URL}/schedule/first-available-date`, {
    params: {
      scheduleTemplateIds: scheduleTemplateIds.join(','),
      fromDate,
      ...(buildingId ? { buildingId } : {}),
      ...(appointmentsCategory ? { appointmentsCategory } : {}),
      qtyAppointments,
      isNewPatient,
    },
  }).then((response) => response.data);
}

export function getScheduleSetupTags(medicalScheduleSetupId: number): Promise<ScheduleTagDTO[]> {
  return axiosInstance.get<ScheduleTagDTO[]>(`${BASE_URL}/schedule-setups/${medicalScheduleSetupId}/tags`)
    .then((response) => response.data);
}
